import { Info, TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';

import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { CheckboxMark } from '@/design-system/ui/Checkbox';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader } from '@/design-system/ui/Dialog';
import { Input } from '@/design-system/ui/Input';
import { SegmentedControl } from '@/design-system/ui/SegmentedControl';
import { SimpleSelect } from '@/design-system/ui/Select';
import {
  AUDIENCE_METHODS,
  type AudienceCount,
  type AudienceDraft,
  MAX_AUDIENCE_NOTE,
  audienceDraftOf,
  checkAudience,
} from '@escala/domain/audience';
import { addDays, formatDayMonth, formatNumericDate, toISODate } from '@escala/domain/dates';
import type { AudienceMethod, ScheduleEvent } from '@escala/domain/types';
import { useStore } from '@/store/AppStore';

/** Quantos dias para trás o "trocar de culto" oferece. */
const PICK_DAYS = 60;

const FIELDS: Record<AudienceCount, { label: string; hint: string }> = {
  total: {
    label: 'Público presencial total',
    hint: 'Todas as pessoas presentes no local, incluindo crianças.',
  },
  members: { label: 'Membros presentes', hint: 'Contagem agregada, sem nomes.' },
  firstTime: { label: 'Visitantes de primeira vez', hint: '' },
  recurring: { label: 'Visitantes recorrentes', hint: '' },
  kids: { label: 'Crianças presentes', hint: 'Já incluídas no total presencial; não são somadas de novo.' },
  online: { label: 'Público online', hint: 'Separado do presencial. Ex.: pico de pessoas na transmissão.' },
};

function at(ms: number): string {
  const d = new Date(ms);

  return `${formatNumericDate(toISODate(d))} às ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Quem registrou e quem editou depois — para a auditoria da administração. */
export function audienceAudit(event: ScheduleEvent): string {
  const a = event.audience;

  if (!a) return '';

  const edited = a.updatedBy && a.updatedAt ? ` · editado por ${a.updatedBy} em ${at(a.updatedAt)}` : '';

  return `Registrado por ${a.recordedBy} em ${at(a.recordedAt)}${edited}`;
}

/**
 * "Registrar público do culto": as contagens agregadas de um culto que já aconteceu, sem
 * lista de nomes. Abre do painel do evento (Calendário), de Membresia › Presença nos
 * cultos e do "Registrar público do culto" da Membresia — este com a troca de culto
 * (`pick`). Fechar com alterações pergunta antes de descartar.
 */
export default function AudienceDialog({
  eventId,
  pick = false,
  onClose,
}: {
  eventId: string;
  pick?: boolean;
  onClose: () => void;
}) {
  const { state, actions, today } = useStore();
  const [currentId, setCurrentId] = useState(eventId);
  const event = state.events.find((e) => e.id === currentId);
  const [draft, setDraft] = useState<AudienceDraft>(() => audienceDraftOf(event?.audience ?? null));
  const [initial, setInitial] = useState(() => JSON.stringify(draft));
  const [showErrors, setShowErrors] = useState(false);
  const [confirmOver, setConfirmOver] = useState(false);
  const [ack, setAck] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const baseId = useId();

  if (!event) return null;

  const check = checkAudience(draft);
  const dirty = JSON.stringify(draft) !== initial;
  const errors = showErrors ? check.errors : {};
  const total = check.input?.total ?? (Number(draft.total) || null);

  const set = (patch: Partial<AudienceDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setConfirmOver(false);
    setAck(false);
    setLeaving(false);
    setError(null);
  };

  const switchTo = (id: string) => {
    const next = state.events.find((e) => e.id === id);
    const fresh = audienceDraftOf(next?.audience ?? null);

    setCurrentId(id);
    setDraft(fresh);
    setInitial(JSON.stringify(fresh));
    setShowErrors(false);
    setConfirmOver(false);
    setAck(false);
    setError(null);
  };

  const close = () => {
    if (dirty && !leaving) setLeaving(true);
    else onClose();
  };

  const save = () => {
    setShowErrors(true);

    if (!check.input) return;
    if (check.segmentOver && !ack) {
      setConfirmOver(true);

      return;
    }

    const refused = actions.recordAudience(event.id, check.input);

    if (refused) setError(refused);
    else onClose();
  };

  const options = state.events
    .filter((e) => e.date <= today && e.date >= addDays(today, -PICK_DAYS))
    .sort((a, b) => (b.date + b.time).localeCompare(a.date + a.time))
    .map((e) => ({
      value: e.id,
      label: `${formatDayMonth(e.date)} · ${e.title} ${e.time}${e.audience ? '' : ' — não registrado'}`,
    }));

  const field = (key: AudienceCount, required = false) => {
    const id = `${baseId}-${key}`;
    const hint = FIELDS[key].hint;
    const message = errors[key];

    return (
      <div key={key} className="flex flex-col gap-1.5">
        <label htmlFor={id} className="text-small font-semibold text-ink">
          {FIELDS[key].label}
          {required ? <span className="text-status-conflict"> *</span> : null}
        </label>
        <Input
          id={id}
          inputMode="numeric"
          autoComplete="off"
          value={draft[key]}
          invalid={!!message}
          aria-describedby={hint || message ? `${id}-hint` : undefined}
          onChange={(e) => set({ [key]: e.target.value.replace(/[^\d]/g, '') })}
        />
        {message ? (
          <span id={`${id}-hint`} role="alert" className="text-meta text-status-conflict">
            {message}
          </span>
        ) : null}
        {!message && hint ? (
          <span id={`${id}-hint`} className="text-meta text-ink-subtle">
            {hint}
          </span>
        ) : null}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="w-[min(560px,calc(100vw-24px))]">
        <DialogHeader
          title={event.audience ? 'Editar público do evento' : 'Registrar público do evento'}
          description={`${event.title} · ${formatDayMonth(event.date)} às ${event.time}`}
        />
        <DialogBody>
          {pick && options.length > 1 ? (
            <div className="flex flex-col gap-1.5">
              <label htmlFor={`${baseId}-pick`} className="text-small font-semibold">
                Evento
              </label>
              <SimpleSelect
                id={`${baseId}-pick`}
                value={event.id}
                onValueChange={switchTo}
                options={options}
              />
            </div>
          ) : null}

          <Alert tone="info">
            <Info aria-hidden />
            <span>
              Este registro contabiliza o público do evento. Não é necessário identificar individualmente quem
              esteve presente.
            </span>
          </Alert>

          {field('total', true)}

          <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
            <legend className="mb-1 text-control font-bold">Segmentação (opcional)</legend>
            <div className="grid gap-3 sm:grid-cols-3">
              {(['members', 'firstTime', 'recurring'] as const).map((k) => field(k))}
            </div>
            {total !== null && check.segmented > 0 ? (
              <p
                className={
                  check.segmentOver ? 'm-0 text-meta text-status-partial' : 'm-0 text-meta text-ink-subtle'
                }
              >
                Membros + visitantes: {check.segmented.toLocaleString('pt-BR')} de{' '}
                {total.toLocaleString('pt-BR')} presentes
                {check.segmented < total
                  ? ` · ${(total - check.segmented).toLocaleString('pt-BR')} sem segmentação`
                  : ''}
              </p>
            ) : null}
          </fieldset>

          <div className="grid gap-3 sm:grid-cols-2">
            {(['kids', 'online'] as const).map((k) => field(k))}
          </div>

          <div className="flex flex-col gap-1.5">
            <span id={`${baseId}-method`} className="text-small font-semibold">
              Método de contagem
            </span>
            <SegmentedControl<AudienceMethod>
              aria-label="Método de contagem"
              size="sm"
              className="flex-wrap"
              value={draft.method}
              onValueChange={(method) => set({ method })}
              options={AUDIENCE_METHODS}
            />
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-small font-semibold">Observação (opcional)</span>
            <textarea
              rows={2}
              maxLength={MAX_AUDIENCE_NOTE}
              value={draft.note}
              onChange={(e) => set({ note: e.target.value })}
              className="w-full min-w-0 resize-y rounded-control border border-line-input bg-white px-3 py-2.5 text-control text-ink placeholder:text-ink-placeholder"
              placeholder="Ex.: chuva forte, evento com batismo"
            />
            <span className="text-meta text-ink-subtle">
              Só o que ajuda a entender o número. Não inclua nomes nem dados pessoais.
            </span>
          </label>

          {event.audience ? <p className="m-0 text-meta text-ink-subtle">{audienceAudit(event)}</p> : null}

          {confirmOver ? (
            <Alert tone="warning" role="alert" className="flex-col">
              <span className="flex gap-2.5">
                <TriangleAlert
                  aria-hidden
                  className="mt-0.5 size-4 shrink-0 stroke-[1.8] text-status-partial"
                />
                Membros e visitantes somam {check.segmented.toLocaleString('pt-BR')}, mais que o total
                presencial ({(total ?? 0).toLocaleString('pt-BR')}). Se a contagem foi estimada, confirme para
                salvar assim.
              </span>
              <button
                type="button"
                role="checkbox"
                aria-checked={ack}
                onClick={() => setAck((a) => !a)}
                className="flex items-center gap-2 self-start text-small font-semibold text-ink"
              >
                <CheckboxMark checked={ack} />
                Confirmo que os números estão corretos
              </button>
            </Alert>
          ) : null}

          {leaving ? (
            <Alert tone="warning" role="alert" className="flex-wrap items-center">
              <span className="min-w-50 flex-1">Descartar as alterações deste registro?</span>
              <Button size="sm" variant="secondary" onClick={() => setLeaving(false)}>
                Continuar editando
              </Button>
              <Button size="sm" variant="danger" onClick={onClose}>
                Descartar
              </Button>
            </Alert>
          ) : null}

          {error ? <Alert tone="danger">{error}</Alert> : null}
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={close}>
            Cancelar
          </Button>
          <Button onClick={save} disabled={confirmOver && !ack}>
            {event.audience ? 'Salvar alterações' : 'Salvar registro'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
